import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { highlightSearchTerm } from '../../../components/custom_components/Search/Search'
import { EmployeeSearch } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { SmartAvatar } from '../../avatar/SmartAvatar'
import { Select, Spin } from 'antd'

const { Option } = Select

interface EmployeeSearchSelectProps {
  searchTerm: string
  onSearch: (value: string) => void
  onClear: () => void
  employees: EmployeeSearch[]
  loading: boolean
  shouldShowOptions: boolean
  onOptionClick: (employee: EmployeeSearch) => void
  placeholder: string
  selectedEmployee?: EmployeeSearch | null
  size: 'small' | 'default'
  optionMaxWidth?: number
}

export const EmployeeSearchSelect: FC<EmployeeSearchSelectProps> = ({
  searchTerm,
  onSearch,
  onClear,
  employees,
  loading,
  shouldShowOptions,
  onOptionClick,
  placeholder,
  selectedEmployee,
  size,
  optionMaxWidth,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()

  // Render function for employee options
  const renderOptions = useCallback(
    (employee: EmployeeSearch) => (
      <Option
        key={`employee-${employee.id}`}
        value={employee.id}
        label={computeFullname(user.nameDisplayPreference as NameDisplayPreference, employee.firstName, employee.lastName)}
      >
        <div className="option" onClick={() => onOptionClick(employee)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SmartAvatar firstName={employee.firstName} lastName={employee.lastName} employeeId={employee.id} size="small" />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    display: 'inline',
                    marginLeft: '4px',
                    ...(optionMaxWidth !== undefined ? { maxWidth: `${optionMaxWidth}px` } : {}),
                  }}
                  className="option-text"
                >
                  {highlightSearchTerm(
                    computeFullname(user.nameDisplayPreference as NameDisplayPreference, employee.firstName, employee.lastName),
                    searchTerm,
                  )}
                </span>
                <span style={{ color: '#666', fontSize: '12px', display: 'inline', marginLeft: '4px' }}>
                  {highlightSearchTerm(employee.companyName, searchTerm)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Option>
    ),
    [onOptionClick, searchTerm, user.nameDisplayPreference, optionMaxWidth],
  )

  const notFoundContent = useMemo(() => {
    if (loading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
          <Spin size="small" style={{ marginRight: '8px' }} />
          <span>{t('navigation.search.loading')}</span>
        </div>
      )
    } else if (searchTerm.length > 2) {
      return t('navigation.search.noResults')
    } else if (searchTerm.length > 0 && searchTerm.length <= 2) {
      return t('navigation.search.startTyping')
    }
    return t('navigation.search.startTyping')
  }, [searchTerm, loading, t])

  return (
    <Select
      showSearch
      placeholder={placeholder}
      value={
        selectedEmployee
          ? {
              key: selectedEmployee.id,
              label: computeFullname(
                user.nameDisplayPreference as NameDisplayPreference,
                selectedEmployee.firstName,
                selectedEmployee.lastName,
              ),
            }
          : undefined
      }
      labelInValue
      onSearch={onSearch}
      onChange={(value) => {
        if (value && typeof value === 'object' && value.key) {
          const employee = employees.find((emp) => emp.id === value.key)
          if (employee) {
            onOptionClick(employee)
          }
        } else {
          onClear()
        }
      }}
      allowClear
      filterOption={false}
      loading={loading}
      notFoundContent={notFoundContent}
      style={{ width: '100%' }}
      size={size}
      dropdownMatchSelectWidth={false}
    >
      {shouldShowOptions &&
        employees
          .filter((employee) => {
            if (selectedEmployee && employee.id === selectedEmployee.id) {
              return false
            }
            return true
          })
          .map(renderOptions)}
    </Select>
  )
}
